import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cake, Mail, RefreshCcw, MapPin, AlertTriangle, Printer, CheckCircle2, Circle, Trophy } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const RANGES = [7, 14, 30];

export default function AdminBirthdays() {
  const [days, setDays] = useState(14);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState({ total_sent: 0, sent_this_year: 0, pending_this_year: 0 });

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get("/admin/birthdays/gifts/stats");
      setStats(r.data);
    } catch {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/upcoming-birthdays?days=${days}`);
      setRows(r.data);
      loadStats();
    } catch (e) { toast.error("Failed to load birthdays"); }
    finally { setLoading(false); }
  }, [days, loadStats]);

  useEffect(() => { load(); }, [load]);

  const sendDigest = async () => {
    setSending(true);
    try {
      const r = await api.post(`/admin/upcoming-birthdays/send-digest?days=${days}`);
      if (r.data.sent) toast.success(`Digest sent to ${(r.data.recipients || []).join(", ")}`);
      else toast.warning(r.data.reason || "Not sent");
    } catch (e) { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-birthdays">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl">Birthday Gifts</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
            Customers with birthdays in the next {days} days. Generate a printable address label, stamp the envelope, drop it in the mail, then hit <span className="text-white">Mark as Sent</span> so they stop appearing in your daily reminder.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {RANGES.map((n) => (
            <button key={n} onClick={() => setDays(n)}
                    data-testid={`bday-range-${n}`}
                    className={`text-xs px-3 py-2 rounded border transition-colors ${days === n ? "bg-[var(--cl-primary)] border-[var(--cl-primary)] text-white" : "border-white/15 text-zinc-300 hover:border-[var(--cl-orange)]"}`}>
              Next {n}d
            </button>
          ))}
          <button onClick={load} data-testid="bday-refresh" className="btn-outline text-xs">
            <RefreshCcw size={12} className="inline mr-1 -mt-0.5" /> Refresh
          </button>
          <button onClick={sendDigest} disabled={sending || !rows.length}
                  data-testid="bday-send-digest"
                  className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed">
            <Mail size={12} className="inline mr-1 -mt-0.5" /> {sending ? "Sending…" : "Send digest now"}
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="gift-stats">
        <StatCard testid="stat-total" icon={<Trophy size={16} className="text-[var(--cl-orange)]" />}
                  label="Gifts Sent All-Time" value={stats.total_sent} />
        <StatCard testid="stat-year" icon={<Cake size={16} className="text-[var(--cl-orange)]" />}
                  label={`Sent in ${new Date().getFullYear()}`} value={stats.sent_this_year} />
        <StatCard testid="stat-pending" icon={<Circle size={16} className="text-zinc-400" />}
                  label="Pending This Year" value={stats.pending_this_year} />
      </div>

      {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="border border-dashed border-white/10 rounded-lg py-16 text-center" data-testid="bday-empty">
          <Cake size={40} className="mx-auto text-[var(--cl-orange)]" />
          <h2 className="font-display text-3xl mt-4">All caught up 🎉</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            No upcoming birthdays in the next {days} days that still need a gift. Customers marked as sent are hidden from this list.
          </p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((c) => <BirthdayRow key={c.id} c={c} onChange={load} />)}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, testid }) {
  return (
    <div className="border border-white/10 rounded p-4 bg-[var(--cl-surface)]" data-testid={testid}>
      <div className="flex items-center gap-2 text-label text-zinc-500">
        {icon} {label}
      </div>
      <div className="font-display text-4xl mt-2">{value}</div>
    </div>
  );
}

function BirthdayRow({ c, onChange }) {
  const [busy, setBusy] = useState(false);
  const [gift, setGift] = useState(null);
  const addr = c.shipping_address;

  useEffect(() => {
    api.get(`/admin/birthdays/${c.id}/gift-history`)
      .then((r) => {
        const currentYear = new Date().getFullYear();
        const thisYear = (r.data || []).find((g) => g.year === currentYear);
        if (thisYear) setGift(thisYear);
      })
      .catch(() => {});
  }, [c.id]);

  const openLabel = async () => {
    if (!addr) return toast.error("No shipping address on file — customer needs to add one first");
    setBusy(true);
    try {
      const r = await api.post(`/admin/birthdays/${c.id}/gift-label-pdf`, null, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      // Refresh the local gift state so we get the newly created record
      const hist = await api.get(`/admin/birthdays/${c.id}/gift-history`);
      const currentYear = new Date().getFullYear();
      setGift((hist.data || []).find((g) => g.year === currentYear) || null);
      toast.success("Label opened in new tab · stamp it, mail it, then mark it sent");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not generate label";
      toast.error(msg, { duration: 8000 });
    } finally { setBusy(false); }
  };

  const toggleSent = async (sent) => {
    if (!gift?.id) return toast.error("Generate the label first so we have a record to mark");
    setBusy(true);
    try {
      const r = await api.post(`/admin/birthdays/gifts/${gift.id}/mark-sent`, { sent });
      setGift({ ...gift, ...r.data });
      if (sent) {
        toast.success("Marked as sent — great work! Refreshing list…");
        onChange && onChange();
      } else {
        toast.info("Un-marked — the customer will appear on tomorrow's digest again");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const chip =
    c.days_until === 0 ? { text: "TODAY 🎂", color: "bg-[var(--cl-primary)]" } :
    c.days_until === 1 ? { text: "TOMORROW", color: "bg-orange-500" } :
    { text: `in ${c.days_until} days`, color: "bg-white/10" };
  const favs = [c.favorite_player, c.favorite_team].filter(Boolean).join(" · ");
  const sent = !!gift?.marked_sent;

  return (
    <div className={`border rounded p-4 flex flex-wrap justify-between gap-4 transition-colors ${sent ? "border-green-700/40 bg-green-950/10" : "border-white/10 bg-[var(--cl-surface)]"}`}
         data-testid={`bday-row-${c.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="font-medium text-white">{c.name || "—"}</div>
          <div className="text-xs text-zinc-500">{c.email}</div>
          <span className={`${chip.color} text-white text-[10px] tracking-widest uppercase px-2 py-1 rounded-full`}>
            {chip.text}
          </span>
        </div>
        <div className="text-sm text-zinc-300 mt-2 flex items-center gap-2">
          <Cake size={14} className="text-[var(--cl-orange)]" /> {c.birthday_this_year}
        </div>
        {addr ? (
          <div className="text-xs text-zinc-500 mt-2 flex items-start gap-2">
            <MapPin size={12} className="mt-0.5" />
            <span>{addr.line1}, {addr.city} {addr.state} {addr.postal_code}{addr.country ? `, ${addr.country}` : ""}</span>
          </div>
        ) : (
          <div className="text-xs text-red-400 mt-2 flex items-center gap-2">
            <AlertTriangle size={12} /> No shipping address on file
          </div>
        )}
        {favs && <div className="text-xs text-zinc-600 mt-1">{favs}</div>}
        {gift?.label_generated_at && (
          <div className="text-[10px] text-zinc-600 mt-2">
            Label generated {new Date(gift.label_generated_at).toLocaleString()}
          </div>
        )}
        {gift?.sent_at && (
          <div className="text-xs text-green-400 mt-2 flex items-center gap-2">
            <CheckCircle2 size={12} /> Sent on {new Date(gift.sent_at).toLocaleDateString()}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 self-center flex flex-col gap-2 min-w-[160px]">
        <button onClick={openLabel} disabled={busy || !addr}
                data-testid={`bday-print-label-${c.id}`}
                className="btn-outline text-xs whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed">
          <Printer size={12} className="inline mr-1 -mt-0.5" />
          {gift?.label_generated_at ? "Reprint Label" : "Print Address Label"}
        </button>
        <button onClick={() => toggleSent(!sent)} disabled={busy}
                data-testid={`bday-mark-sent-${c.id}`}
                className={`text-xs px-3 py-2 rounded font-semibold whitespace-nowrap transition-colors disabled:opacity-40 ${sent ? "bg-green-600 hover:bg-green-500 text-white" : "bg-[var(--cl-primary)] hover:bg-[var(--cl-primary-hover,#e6342a)] text-white"}`}>
          {sent
            ? <><CheckCircle2 size={12} className="inline mr-1 -mt-0.5" /> Sent — click to undo</>
            : <><Circle size={12} className="inline mr-1 -mt-0.5" /> Mark as Sent</>}
        </button>
        <Link to={`/admin/customers?open=${c.id}`}
              data-testid={`bday-open-${c.id}`}
              className="text-[10px] text-center text-zinc-500 hover:text-white underline">
          Open profile →
        </Link>
      </div>
    </div>
  );
}
